import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './DoctoralCommitteeApi';
import { DoctoralCommittee, DoctoralCommitteeRequest } from './DoctoralCommitteeTypes';

const doctoralCommitteeKey = getQueryKey('doctoralCommittee');

export function getDoctoralCommitteePagedOptions(
  params: PagedParams,
): UseSuspenseQueryOptions<PagedResponse<DoctoralCommittee>> {
  return {
    queryKey: doctoralCommitteeKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getDoctoralCommitteeOptions(doctoralCommitteeId: Id): UseSuspenseQueryOptions<DoctoralCommittee> {
  return {
    queryKey: doctoralCommitteeKey.one(doctoralCommitteeId),
    queryFn: () => api.get(doctoralCommitteeId),
  };
}

export function useDoctoralCommitteeEdit(doctoralCommitteeId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DoctoralCommitteeRequest) => api.edit(doctoralCommitteeId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: doctoralCommitteeKey.root(),
      }),
  });
}

export function useDoctoralCommitteeCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DoctoralCommitteeRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: doctoralCommitteeKey.root(),
      }),
  });
}

export function useDoctoralCommitteeDelete(doctoralCommitteeId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(doctoralCommitteeId),
    onSuccess: () => invalidateAfterDelete(queryClient, doctoralCommitteeKey, doctoralCommitteeId),
  });
}
